import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { notify } from '../../utils/notifications';
import {
  ArrowLeft, Edit, Trash2, UserCircle, Mail, Phone, Calendar,
  CheckCircle, XCircle, Shield, MapPin, Bus, Clock, Key, Activity
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const AutostaDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [autista, setAutista] = useState(null);
  const [corse, setCorse] = useState([]);
  const [logAccessi, setLogAccessi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLineeModal, setShowLineeModal] = useState(false);
  const [lineeDisponibili, setLineeDisponibili] = useState([]);
  const [lineeSelezionate, setLineeSelezionate] = useState([]);

  useEffect(() => {
    caricaDatiAutista();
  }, [id]);

  const caricaDatiAutista = async () => {
    try {
      setLoading(true);
      
      // Carica autista
      const autistaRes = await api.get(`/autenticazione/utenti/${id}/`);
      setAutista(autistaRes.data);

      // Carica corse dell'autista
      const corseRes = await api.get(`/trasporti/corse/?autista=${id}`);
      setCorse(corseRes.data.results || corseRes.data);

      // Carica log accessi
      try {
        const logRes = await api.get(`/autenticazione/log-accessi/?utente=${id}`);
        setLogAccessi((logRes.data.results || logRes.data).slice(0, 10)); // Ultimi 10
      } catch (err) {
        console.log('Log accessi non disponibili');
      }

    } catch (error) {
      console.error('Errore caricamento autista:', error);
      notify.error('Errore nel caricamento dei dati dell\'autista');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo autista?')) return;
    
    const toastId = notify.loading('Eliminazione...');
    
    try {
      await api.delete(`/autenticazione/utenti/${id}/`);
      notify.update(toastId, { type: 'success', render: 'Autista eliminato con successo!' });
      setTimeout(() => navigate('/admin/autisti'), 500);
    } catch (error) {
      console.error('Errore eliminazione autista:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'eliminazione dell\'autista' });
    }
  };

  const handleToggleAttivo = async () => {
    const toastId = notify.loading(autista.is_active ? 'Disattivazione...' : 'Attivazione...');
    
    try {
      await api.post(`/autenticazione/utenti/${id}/attiva_disattiva/`);
      notify.update(toastId, { 
        type: 'success', 
        render: autista.is_active ? 'Autista disattivato!' : 'Autista attivato!' 
      });
      await caricaDatiAutista();
    } catch (error) {
      console.error('Errore toggle attivo:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'operazione' });
    }
  };

  const handleResetPassword = async () => {
    const newPassword = prompt(`Inserisci la nuova password per ${autista.username}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 4) {
      notify.error('La password deve essere di almeno 4 caratteri');
      return;
    }
    
    const toastId = notify.loading('Reset password...');
    
    try {
      await api.post(`/autenticazione/utenti/${id}/reset_password/`, { nuova_password: newPassword });
      notify.update(toastId, { type: 'success', render: 'Password reimpostata con successo!' });
    } catch (error) {
      console.error('Errore reset password:', error);
      const errorMsg = error.response?.data?.errore || 'Errore nel reset della password';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const caricaLinee = async () => {
    try {
      const response = await api.get('/trasporti/linee/');
      const linee = response.data.results || response.data || [];
      setLineeDisponibili(linee);
      
      // Pre-seleziona le linee già assegnate
      if (autista?.linee_assegnate) {
        setLineeSelezionate(autista.linee_assegnate.map(l => l.id));
      }
    } catch (error) {
      console.error('Errore caricamento linee:', error);
      notify.error('Errore nel caricamento delle linee');
    }
  };

  const handleOpenLineeModal = () => {
    setShowLineeModal(true);
    caricaLinee();
  };

  const handleToggleLinea = (lineaId) => {
    setLineeSelezionate(prev => {
      if (prev.includes(lineaId)) {
        return prev.filter(id => id !== lineaId);
      } else {
        return [...prev, lineaId];
      }
    });
  };

  const handleSalvaLinee = async () => {
    const toastId = notify.loading('Salvataggio linee...');
    
    try {
      await api.patch(`/autenticazione/utenti/${id}/`, {
        linee_assegnate: lineeSelezionate
      });
      notify.update(toastId, { type: 'success', render: 'Linee aggiornate con successo!' });
      setShowLineeModal(false);
      await caricaDatiAutista();
    } catch (error) {
      console.error('Errore salvataggio linee:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nel salvataggio delle linee' });
    }
  };

  const getStatoCorsaBadge = (stato) => {
    switch (stato) {
      case 'PROGRAMMATA': return 'bg-yellow-100 text-yellow-800';
      case 'IN_CORSO': return 'bg-blue-100 text-blue-800';
      case 'COMPLETATA': return 'bg-green-100 text-green-800';
      case 'CANCELLATA': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calcola statistiche corse
  const corsePassate = corse.filter(c => new Date(c.data) <= new Date());
  const corseCompletate = corsePassate.filter(c => c.stato === 'COMPLETATA');

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!autista) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <UserCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Autista non trovato</h2>
          <button
            onClick={() => navigate('/admin/autisti')}
            className="text-blue-600 hover:text-blue-700"
          >
            Torna alla lista autisti
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-5">
          <button
            onClick={() => navigate('/admin/autisti')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna agli autisti
          </button>
          
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div className="flex items-center gap-4">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-3xl ${
                autista.is_active ? 'bg-blue-600' : 'bg-gray-400'
              }`}>
                {autista.first_name ? autista.first_name.charAt(0).toUpperCase() : autista.username.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {autista.first_name && autista.last_name 
                    ? `${autista.first_name} ${autista.last_name}`
                    : autista.username
                  }
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">@{autista.username}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleToggleAttivo}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold ${
                  autista.is_active 
                    ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {autista.is_active ? (
                  <>
                    <XCircle className="w-4 h-4" />
                    Disattiva
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Attiva
                  </>
                )}
              </button>
              <button
                onClick={handleResetPassword}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Key className="w-4 h-4" />
                Reset Password
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <Trash2 className="w-4 h-4" />
                Elimina
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Colonna sinistra */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dettagli Autista */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <UserCircle className="w-4 h-4 text-blue-600" />
                Informazioni Personali
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nome Completo</p>
                  <p className="font-semibold text-gray-900">
                    {autista.first_name && autista.last_name 
                      ? `${autista.first_name} ${autista.last_name}`
                      : 'Non specificato'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Username</p>
                  <p className="font-semibold text-gray-900">@{autista.username}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <p className="font-semibold text-gray-900">{autista.email || 'Non specificata'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefono</p>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <p className="font-semibold text-gray-900">{autista.telefono || 'Non specificato'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Stato Account</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full font-semibold ${
                    autista.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {autista.is_active ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Attivo
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3" />
                        Inattivo
                      </>
                    )}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sicurezza</p>
                  {autista.richiede_2fa ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full font-semibold bg-purple-100 text-purple-800">
                      <Shield className="w-3 h-3" />
                      2FA Attivo
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">2FA Non attivo</span>
                  )}
                </div>
                {autista.ultimo_accesso_ip && (
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500">Ultimo IP di accesso</p>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <p className="font-mono text-sm text-gray-900">{autista.ultimo_accesso_ip}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Linee Assegnate */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-purple-600" />
                  Linee Assegnate ({autista.linee_assegnate?.length || 0})
                </h2>
                <button
                  onClick={handleOpenLineeModal}
                  className="px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm"
                >
                  Modifica Linee
                </button>
              </div>
              {autista.linee_assegnate && autista.linee_assegnate.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {autista.linee_assegnate.map(linea => (
                    <div key={linea.id} className="border border-purple-200 rounded-lg p-3 hover:border-purple-400 transition">
                      <div className="flex items-center gap-2 mb-2">
                        <Bus className="w-4 h-4 text-purple-600" />
                        <span className="font-semibold text-gray-900">{linea.codice}</span>
                      </div>
                      <p className="text-sm text-gray-600">{linea.nome}</p>
                      {linea.descrizione && (
                        <p className="text-xs text-gray-500 mt-1">{linea.descrizione}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  Nessuna linea assegnata. Clicca su "Modifica Linee" per assegnare delle linee a questo autista.
                </p>
              )}
            </div>

            {/* Storico Corse */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-600" />
                Storico Corse ({corsePassate.length})
              </h2>
              {corsePassate.length > 0 ? (
                <div className="space-y-3">
                  {corsePassate.slice(0, 5).map(corsa => (
                    <div key={corsa.id} className="border-l-4 border-gray-300 pl-4 py-2 hover:bg-gray-50 rounded">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-gray-900">{corsa.linea_nome}</p>
                          <p className="text-sm text-gray-600">
                            {format(new Date(corsa.data), 'd MMM yyyy', { locale: it })}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full font-semibold ${getStatoCorsaBadge(corsa.stato)}`}>
                          {corsa.stato}
                        </span>
                      </div>
                    </div>
                  ))}
                  {corsePassate.length > 5 && (
                    <p className="text-sm text-gray-500 text-center">
                      ...e altre {corsePassate.length - 5} corse
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Nessuna corsa passata</p>
              )}
            </div>

            {/* Log Accessi */}
            {logAccessi.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-600" />
                  Ultimi Accessi
                </h2>
                <div className="space-y-2">
                  {logAccessi.map((log, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${log.successo ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {log.successo ? 'Accesso riuscito' : 'Accesso fallito'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {log.ip_address} • {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm', { locale: it })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Note */}
            {autista.note_interne && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Note Interne</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{autista.note_interne}</p>
              </div>
            )}
          </div>

          {/* Colonna destra */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Statistiche</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-blue-100 text-sm">Corse Totali</p>
                  <p className="text-2xl font-bold">{corse.length}</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Completate</p>
                  <p className="text-2xl font-bold text-gray-900">{corseCompletate.length}</p>
                </div>
                {corsePassate.length > 0 && (
                  <div>
                    <p className="text-blue-100 text-sm">Tasso Completamento</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {Math.round((corseCompletate.length / corsePassate.length) * 100)}%
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Info Sistema */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                Info Sistema
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Data Registrazione</p>
                  <p className="font-semibold text-gray-900">
                    {autista.data_creazione ? format(new Date(autista.data_creazione), 'dd MMMM yyyy', { locale: it }) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Ultima Modifica</p>
                  <p className="font-semibold text-gray-900">
                    {autista.data_modifica ? format(new Date(autista.data_modifica), 'dd/MM/yyyy HH:mm', { locale: it }) : 'N/A'}
                  </p>
                </div>
                {autista.last_login && (
                  <div>
                    <p className="text-gray-500">Ultimo Login</p>
                    <p className="font-semibold text-gray-900">
                      {format(new Date(autista.last_login), 'dd/MM/yyyy HH:mm', { locale: it })}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">ID Utente</p>
                  <p className="font-mono text-xs text-gray-900">{autista.id}</p>
                </div>
              </div>
            </div>

            {/* Azioni Rapide */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Azioni Rapide</h3>
              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/admin/autisti`)}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition"
                >
                  <Edit className="w-4 h-4" />
                  Modifica Dati
                </button>
                <button
                  onClick={handleResetPassword}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition"
                >
                  <Key className="w-4 h-4" />
                  Reset Password
                </button>
                <button
                  onClick={handleToggleAttivo}
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    autista.is_active
                      ? 'bg-red-50 text-red-700 hover:bg-red-100'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  {autista.is_active ? (
                    <>
                      <XCircle className="w-4 h-4" />
                      Disattiva Account
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Attiva Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Assegnazione Linee */}
      {showLineeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-purple-600" />
                Assegna Linee a {autista?.first_name || autista?.username}
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                Seleziona le linee che questo autista può guidare
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {lineeDisponibili.length === 0 ? (
                <div className="text-center py-8">
                  <Bus className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">Nessuna linea disponibile nel sistema</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {lineeDisponibili.map(linea => (
                    <label
                      key={linea.id}
                      className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition ${
                        lineeSelezionate.includes(linea.id)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={lineeSelezionate.includes(linea.id)}
                        onChange={() => handleToggleLinea(linea.id)}
                        className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Bus className="w-4 h-4 text-purple-600" />
                          <span className="font-semibold text-gray-900">{linea.codice}</span>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">{linea.nome}</p>
                        {linea.descrizione && (
                          <p className="text-xs text-gray-500">{linea.descrizione}</p>
                        )}
                        {linea.scuole_servite && (
                          <p className="text-xs text-gray-500 mt-1">
                            Scuole: {linea.scuole_servite}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">{lineeSelezionate.length}</span> linea
                {lineeSelezionate.length !== 1 && 'e'} selezionata{lineeSelezionate.length !== 1 && 'e'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLineeModal(false)}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSalvaLinee}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Salva Linee
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AutostaDettaglio;

